import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import AddCommitmentDialog from "../../components/app/AddCommitmentDialog";
import { formatINR, categoryMeta } from "../../lib/format";
import { ArrowRight, ShieldCheck, Plus, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [income, setIncome] = useState({ monthly_income: "", salary_day: "1", income_type: "fixed", available_balance: "", protected_reserve: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [commitments, setCommitments] = useState([]);
  const [position, setPosition] = useState(null);
  const [saving, setSaving] = useState(false);

  const saveIncome = async () => {
    if (!income.monthly_income) { toast.error("Please enter your monthly income."); return; }
    setSaving(true);
    try {
      await api.put("/income-profile", {
        monthly_income: parseFloat(income.monthly_income),
        salary_day: parseInt(income.salary_day) || 1,
        income_type: income.income_type,
        available_balance: income.available_balance ? parseFloat(income.available_balance) : null,
        protected_reserve: income.protected_reserve ? parseFloat(income.protected_reserve) : 0,
      });
      setStep(3);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const loadCommitments = async () => {
    const { data } = await api.get("/commitments"); setCommitments(data);
  };

  const goReview = async () => {
    const { data } = await api.get("/dashboard"); setPosition(data.position); setStep(4);
  };

  const finish = async () => {
    try { await api.post("/onboarding/complete"); await refresh(); nav("/app"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const skipSetup = async () => {
    try { await api.post("/onboarding/complete"); await refresh(); } catch (e) {}
    nav("/app");
  };

  const progress = (step / 4) * 100;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white/70 backdrop-blur-md">
        <div className="max-w-lg mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Step {step} of 4</span>
            <button onClick={skipSetup} className="text-sm text-mint hover:underline" data-testid="onb-skip-setup">Skip</button>
          </div>
        </div>
        <div className="h-1 bg-secondary"><div className="h-full bg-mint transition-all duration-500" style={{ width: `${progress}%` }} /></div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-10">
        {step === 1 && (
          <div className="animate-fade-up text-center">
            <div className="h-16 w-16 rounded-2xl bg-mint-light text-mint flex items-center justify-center mx-auto"><ShieldCheck size={30} /></div>
            <h1 className="font-heading text-3xl font-light text-ink mt-6">Welcome to COMMITMENTT</h1>
            <p className="text-slatey mt-4 leading-relaxed">
              Know what your money is already committed to — before you spend what's left. COMMITMENTT brings your EMIs, rent, subscriptions, insurance and bills into one place so you can see what's coming and plan ahead.
            </p>

            <div className="mt-7 rounded-2xl border border-border bg-secondary/40 p-5 text-left" data-testid="onb-founder-note">
              <p className="text-xs tracking-[0.16em] uppercase font-semibold text-mint">A note from Aleem</p>
              <p className="text-sm text-slatey mt-2.5 leading-relaxed">
                COMMITMENTT is still in Early Access. Your genuine usage and feedback will directly help shape what we build next. Use it naturally, and if anything feels confusing, unnecessary or missing, please tell us.
              </p>
              <p className="text-sm text-ink font-medium mt-3">— Aleem, Founder of COMMITMENTT</p>
            </div>

            <Button onClick={() => setStep(2)} className="rounded-full mt-7 bg-navy text-white hover:bg-navy-light h-12 px-8" data-testid="onb-start">
              Continue <ArrowRight size={18} className="ml-1" />
            </Button>
            <div className="mt-3 flex items-center justify-center gap-4">
              <button onClick={skipSetup} className="text-sm text-slatey hover:text-ink" data-testid="onb-explore">Explore first</button>
              <span className="text-border">·</span>
              <button onClick={() => window.open("/contact?category=feedback", "_blank")} className="text-sm text-mint hover:underline" data-testid="onb-share-feedback">Share feedback</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <h1 className="font-heading text-2xl font-medium text-ink">Your income cycle</h1>
            <p className="text-slatey mt-2 text-sm">This is used only to calculate your commitment position. It is never a live bank balance.</p>
            <Card className="rounded-2xl border-border shadow-card-sm p-5 mt-6 space-y-4">
              <div><Label className="text-ink text-sm">Income type</Label>
                <Select value={income.income_type} onValueChange={(v) => setIncome((i) => ({ ...i, income_type: v }))}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="onb-incometype"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fixed">Fixed monthly income</SelectItem><SelectItem value="variable">Variable monthly income</SelectItem><SelectItem value="multiple">Multiple income sources</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-ink text-sm">{income.income_type === "variable" ? "Conservative monthly estimate (₹)" : "Monthly income (₹)"}</Label>
                <Input type="number" value={income.monthly_income} onChange={(e) => setIncome((i) => ({ ...i, monthly_income: e.target.value }))} className="mt-1 rounded-xl" data-testid="onb-income" /></div>
              <div><Label className="text-ink text-sm">Salary / income date</Label>
                <Input type="number" min={1} max={28} value={income.salary_day} onChange={(e) => setIncome((i) => ({ ...i, salary_day: e.target.value }))} className="mt-1 rounded-xl" data-testid="onb-salaryday" /></div>
              <div><Label className="text-ink text-sm">Current available balance (₹) <span className="text-muted-foreground font-normal">optional</span></Label>
                <Input type="number" value={income.available_balance} onChange={(e) => setIncome((i) => ({ ...i, available_balance: e.target.value }))} className="mt-1 rounded-xl" data-testid="onb-balance" /></div>
              <div><Label className="text-ink text-sm">Minimum protected reserve (₹) <span className="text-muted-foreground font-normal">optional</span></Label>
                <Input type="number" value={income.protected_reserve} onChange={(e) => setIncome((i) => ({ ...i, protected_reserve: e.target.value }))} className="mt-1 rounded-xl" data-testid="onb-reserve" /></div>
            </Card>
            <Button onClick={saveIncome} disabled={saving} className="w-full rounded-full mt-6 bg-navy text-white hover:bg-navy-light h-12" data-testid="onb-income-next">
              {saving ? "Saving…" : "Continue"}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up">
            <h1 className="font-heading text-2xl font-medium text-ink">Add your commitments</h1>
            <p className="text-slatey mt-2 text-sm">Add manually, paste a message, or upload a screenshot for AI-assisted extraction. You confirm every detail.</p>
            <p className="text-xs text-muted-foreground mt-2">We never ask for SMS inbox access or bank login, and we don't claim a direct bank connection.</p>

            <Button onClick={() => setAddOpen(true)} className="w-full rounded-full mt-5 bg-mint text-white hover:bg-mint/90 h-12" data-testid="onb-add-commitment">
              <Plus size={18} className="mr-1.5" />Add a commitment
            </Button>

            <div className="mt-5 space-y-2">
              {commitments.map((c) => {
                const Icon = categoryMeta(c.category).icon;
                return (
                  <Card key={c.id} className="rounded-xl border-border shadow-card-sm p-3 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center"><Icon size={16} className="text-navy" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-ink truncate">{c.name}</p><p className="text-xs text-muted-foreground">{formatINR(c.amount)} / {c.frequency}</p></div>
                    <Check size={16} className="text-mint" />
                  </Card>
                );
              })}
              {commitments.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm flex flex-col items-center gap-2">
                  <Sparkles size={24} className="text-mint" />No commitments added yet.
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={goReview} className="flex-1 rounded-full bg-secondary text-ink h-12" data-testid="onb-skip">
                {commitments.length ? "Review position" : "Skip for now"}
              </Button>
              {commitments.length > 0 && (
                <Button onClick={goReview} className="flex-1 rounded-full bg-navy text-white hover:bg-navy-light h-12" data-testid="onb-review">Continue</Button>
              )}
            </div>
            <AddCommitmentDialog open={addOpen} onOpenChange={setAddOpen} onSaved={loadCommitments} />
          </div>
        )}

        {step === 4 && position && (
          <div className="animate-fade-up">
            <h1 className="font-heading text-2xl font-medium text-ink">Your current commitment position</h1>
            <Card className="rounded-2xl border-none bg-navy text-white shadow-card-lg p-6 mt-6">
              <p className="text-sm text-white/70">Committed this month</p>
              <p className="font-heading text-4xl font-light mt-1 tnum">{formatINR(position.committed_this_month)}</p>
            </Card>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                ["Monthly income", formatINR(position.monthly_income)],
                ["Commitment ratio", `${position.commitment_ratio}%`],
                ["Protected reserve", formatINR(position.protected_reserve)],
                [position.safe_label, formatINR(position.safe_to_spend)],
                ["Active commitments", position.active_count],
                ["Upcoming this cycle", formatINR(position.due_in_cycle)],
              ].map(([l, v]) => (
                <Card key={l} className="rounded-xl border-border shadow-card-sm p-4">
                  <p className="text-xs text-muted-foreground">{l}</p>
                  <p className="font-heading text-lg font-medium text-ink mt-1 tnum">{v}</p>
                </Card>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4">Estimates are based on the information you provide and confirm. Not a live bank balance or financial advice.</p>
            <Button onClick={finish} className="w-full rounded-full mt-6 bg-navy text-white hover:bg-navy-light h-12" data-testid="onb-open-dashboard">
              Open My Dashboard <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
